export interface IDependentData {
    deferred: ng.IDeferred<any>;
    loadData: any[];
    areDependenciesReady: () => boolean;
    isFinished: boolean;
    onPromiseProgressUpdated?: (isFinished: boolean) => void;
}

export class DependentDataService {
    private dependentData: IDependentData[];

    public static $inject = ['$injector', '$q'];

    constructor(private $injector: any, private $q: ng.IQService) {
        this.dependentData = [];
    }

    /**
     * @description Checks every dependent data config that has been added, whether the dependencies are loaded and ready and if so, will call the load data arrow function.
     * @returns {angular.IPromise<any>} A promise that will resolve when all triggered promises from this single method invocation are finished. If none are triggered, returns
     *  a promise that is already resolved.
     */
    public checkAndLoadDependentData(): ng.IPromise<any> {
        const triggeredPromises: Array<ng.IPromise<any>> = [];
        for (const config of this.dependentData) {
            if (config.isFinished === true) {
                continue;
            }
            if (!config.areDependenciesReady()) {
                continue;
            }
            if (angular.isDefined(config.onPromiseProgressUpdated)) {
                config.onPromiseProgressUpdated(config.isFinished);
            }
            triggeredPromises.push(config.deferred.promise);
            this.$injector.invoke(config.loadData)
            .then((data: any) => {
                config.deferred.resolve(data);
            })
            .catch((error) => {
                config.deferred.reject(error);
            })
            .finally(() => {
                config.isFinished = true;
                if (angular.isDefined(config.onPromiseProgressUpdated)) {
                    config.onPromiseProgressUpdated(config.isFinished);
                }
            });
        }
        return this.$q.all(triggeredPromises);
    }

    /**
     * @description add a new dependent data configuration
     * @param {() => boolean} isDependentDataReady The arrow function used to determine if all dependencies for a given config are loaded and ready.
     * @param {any[]} loadData Should be an angular injection-annotation array. The function in the array should return the promise used for loading 
     *      the actual data.
     * @param {(isFinished: boolean) => void} onPromiseProgressUpdated Optional. The arrow function that will be called whenever the promise has an update.
     *  The function includes a parameter which specifies if the final data is loaded or not. Will be called once when the data begins to load, 
     *      and once when it finishes.
     *  Useful for introducing UI elements during the loading period. ex: Showing the screen lock when the data begins to load, and hiding it once it finishes.
     * @returns {angular.IPromise<any>} The promise that will be resolved whenever the final chain is complete and the data is loaded.
     * The promise will pass the resulting data.
     */
    public addDependentData(
        isDependentDataReady: () => boolean, 
        loadData: any[], 
        onPromiseProgressUpdated?: (isFinished: boolean) => void): ng.IPromise<any> {



        const deferred = this.$q.defer();
        this.dependentData.push({
            areDependenciesReady: isDependentDataReady,
            loadData,
            onPromiseProgressUpdated,
            isFinished: false,
            deferred
        });
        return deferred.promise;
    }
}